import { FC, useMemo, useState, useRef, useEffect } from 'react';
import format from 'date-fns/format';
import { AccessTime as AccessTimeIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';

import { useGetFileUrl } from 'src/use-cases/internalDashboard/useFileResolution';
import { renderInvoiceStatusComponent, renderInvoiceStatusMessages } from '../uploadInvoice/uploadStatusUtils';
import { UploadStatusItemProps } from './types';
import { processingStatuses } from '../uploadInvoice/configs';
import { CustomTooltip } from './CustomTooltip';
import { FileList } from '../shared/FileList';
import { DocumentIconWithModalities } from '../shared/DocumentIconWithModalities';

// Helper function to determine if card should link to invoice or trigger file open
const getIsInvoiceLink = (data: any): boolean => !!(data?.invoiceId && data?.shipmentCount !== data?.processingShipmentCount);

// Helper function to generate invoice link path
const getInvoiceLinkPath = (data: any): string => `/action-details/${data?.invoiceId}?hideBack=1&skipBilledEntityReload=true`;

// Helper to render shipment count with separator
const renderShipmentCount = (label: string, count: number, total: number, hasMoreAfter: boolean) => (
  <>
    <span>
      {label} {count}/{total}
    </span>
    {hasMoreAfter && <span style={{ color: '#DFDFDF' }}>|</span>}
  </>
);

export const UploadStatusItem: FC<UploadStatusItemProps> = ({ data, dataList, errorInvoiceFileProcessingStatuses }) => {
  // Support both single file (data) and multiple files (dataList)
  const files = dataList || (data ? [data] : []);
  const primaryFile = files[0]; // Use first file for main data (invoice number, modalities, etc.)
  const hasMultipleFiles = files.length > 1;

  // Tooltip state with 0.5 second delay
  const [showTooltip, setShowTooltip] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get the user role from the Redux store
  const { refetch: getFileUrl } = useGetFileUrl(primaryFile?.invoiceFileProcessingId);

  /**
   * Memoized value to determine if the current status is a processing status.
   */
  const isProcessing = useMemo(() => processingStatuses.includes(primaryFile?.status) || primaryFile?.processingShipmentCount > 0, [primaryFile]);

  /**
   * Check if status is one that requires special "Other" section styling
   * (Under Review or Resolution statuses)
   */
  const isOtherSectionStatus = useMemo(() => {
    const otherStatuses = ['RESOLVED_AS_NOT_AN_INVOICE', 'RESOLVED_AS_MISSING_REQUIRED_FIELD', 'RESOLVED_AS_INCONSISTENT_SHIPMENT_DATA'];
    const isResolutionStatus = otherStatuses.includes(primaryFile?.status || '');
    const isErrorStatus = errorInvoiceFileProcessingStatuses.includes(primaryFile?.status || '');
    return isResolutionStatus || isErrorStatus;
  }, [primaryFile?.status, errorInvoiceFileProcessingStatuses]);

  const onSeeFile = async () => {
    const result = await getFileUrl();
    if (result?.data) {
      window.open(result?.data, '_blank');
    }
  };

  // Handle mouse move to track cursor position
  const handleMouseMove = (e: React.MouseEvent) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  // Handle mouse enter with 0.5 second delay
  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => {
      setShowTooltip(true);
    }, 500);
  };

  // Handle mouse leave - clear timeout and hide tooltip
  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setShowTooltip(false);
  };

  // Cleanup timeout on unmount
  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  // Determine action type
  const isInvoiceLink = getIsInvoiceLink(primaryFile);
  const isDisabled = primaryFile?.status === 'BEING_UPLOADED';

  // Handle click action
  const handleClick = () => {
    if (isDisabled) return;
    if (!isInvoiceLink) {
      onSeeFile();
    }
  };

  // Render card content
  const cardContent = (
    <div className='relative flex rounded-10 bg-neutral-10 flex-col border border-neutral-20 pt-24 hover:border-primary transition-colors'>
      {/* top info */}
      <div className='flex items-center gap-16 px-16'>
        <DocumentIconWithModalities
          modalities={primaryFile?.modalities}
          isProcessing={isProcessing}
          invoiceFileProcessingId={primaryFile?.invoiceFileProcessingId}
        />
        <div className='flex-1 flex flex-col gap-2'>
          {!primaryFile?.modalities || !primaryFile?.invoiceNumber ? (
            <>{isProcessing && <div className='h-12 w-[130px] bg-[#DFDFDF] rounded-[3px]' />}</>
          ) : (
            <>
              {primaryFile?.modalities && primaryFile?.invoiceNumber && (
                <p className='text-14 xl:text-16 font-bold text-primary flex items-center gap-x-8 select-text cursor-text'>
                  Inv No: {primaryFile?.invoiceNumber}
                </p>
              )}
            </>
          )}
          {/* Show file list when multiple files, single filename when one file */}
          <FileList
            files={files}
            hasMultipleFiles={hasMultipleFiles}
            isOtherSectionStatus={isOtherSectionStatus}
            renderStatusChip={(file, size) => renderInvoiceStatusComponent(file, errorInvoiceFileProcessingStatuses, { size: size || 'small' })}
          />
        </div>

        <div className='ml-auto flex items-center gap-14 justify-end'>
          {/* Show invoice status (APPROVED, FLAGGED, etc.) OR fileResolutionStatus (for Other/Under Review section) OR file status */}
          {primaryFile?.invoiceStatus
            ? renderInvoiceStatusComponent({ invoiceStatus: primaryFile.invoiceStatus }, [], { size: 'medium' })
            : primaryFile?.fileResolutionStatus
              ? // For files with fileResolutionStatus (Other/Under Review section):
                // - UNDER_REVIEW: Show "Under Review" with tooltip (pass to errorProcessingStatuses)
                // - Others (INCONSISTENT_SHIPMENT_DATA, etc.): Show their own status chip
                renderInvoiceStatusComponent(
                  { status: primaryFile.fileResolutionStatus },
                  primaryFile.fileResolutionStatus === 'UNDER_REVIEW' ? ['UNDER_REVIEW'] : [],
                  { size: 'medium' }
                )
              : primaryFile?.status !== 'DUPLICATE' && renderInvoiceStatusComponent(primaryFile, errorInvoiceFileProcessingStatuses, { size: 'medium' })}
        </div>
      </div>
      {/* counter */}
      <div className='flex items-center  px-16 justify-between pt-8 pb-10 mt-16 rounded-8 border border-neutral-20'>
        {!primaryFile?.updatedOn ? (
          <div className='mt-4 h-12 w-[210px] bg-[#DFDFDF] rounded-[3px]' />
        ) : (
          <>
            <p className='mt-2 text-12 xl:text-14 text-neutral-60 flex items-center gap-4'>
              <AccessTimeIcon sx={{ fontSize: '14px', color: '#9F9F9F' }} />
              Updated on {format(new Date(primaryFile?.updatedOn), 'dd-MMM-yyyy')} at {format(new Date(primaryFile?.updatedOn), 'hh.mm a')}
            </p>
            {/* Shipment count information */}
            {primaryFile?.shipmentCount && primaryFile.shipmentCount > 0 ? (
              <div className='mt-2 flex items-center gap-x-8 text-neutral-60 text-10 xl:text-12'>
                {primaryFile?.processingShipmentCount > 0 &&
                  renderShipmentCount(
                    'Processing',
                    primaryFile.processingShipmentCount,
                    primaryFile.shipmentCount,
                    primaryFile?.flaggedShipmentCount > 0 || primaryFile?.resolvedShipmentCount > 0 || primaryFile?.underReviewShipmentCount > 0
                  )}
                {primaryFile?.flaggedShipmentCount > 0 &&
                  renderShipmentCount(
                    'With Audit Exceptions',
                    primaryFile.flaggedShipmentCount,
                    primaryFile.shipmentCount,
                    primaryFile?.resolvedShipmentCount > 0 || primaryFile?.underReviewShipmentCount > 0
                  )}
                {primaryFile?.resolvedShipmentCount > 0 &&
                  renderShipmentCount('Resolved', primaryFile.resolvedShipmentCount, primaryFile.shipmentCount, primaryFile?.underReviewShipmentCount > 0)}
                {primaryFile?.underReviewShipmentCount > 0 &&
                  renderShipmentCount('Under Review', primaryFile.underReviewShipmentCount, primaryFile.shipmentCount, false)}
              </div>
            ) : null}
          </>
        )}
      </div>

      {/* Tooltip rendered inside card */}
      <CustomTooltip show={showTooltip} data={primaryFile} mouseX={mousePosition.x} mouseY={mousePosition.y} />
    </div>
  );

  // Return with conditional wrapper
  if (isInvoiceLink) {
    return (
      <Link
        to={getInvoiceLinkPath(primaryFile)}
        target='_blank'
        className='block w-full'
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ opacity: isDisabled ? 0.4 : 1 }}
      >
        {cardContent}
      </Link>
    );
  }

  return (
    <button
      type='button'
      className='block w-full text-left'
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      disabled={isDisabled}
      style={{ cursor: isDisabled ? 'default' : 'pointer', opacity: isDisabled ? 0.4 : 1 }}
    >
      {cardContent}
    </button>
  );
};
